/**
 * Oznámení — skutečná karta s HISTORIÍ (Filip 2026-08-23: zvoneček nechat,
 * ale je pomíjivý; historie si zaslouží kartu). Čte notifications.list
 * (server drží celou historii per entita), filtry kanál + jen nepřečtená.
 * Rozhodování žádostí zůstává ve zvonečku (decide_surface) — tady je
 * dohledatelnost: co přišlo, kdy, v jakém stavu.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import {
  IMPORTANT_ONLY_EVENT,
  isImportantNotification,
  readImportantOnly,
  writeImportantOnly,
} from '../lib/notif-important';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import type { WidgetDef, WidgetRenderProps } from './types';
import { notificationText } from '../lib/notification-text';
import { sourceKeyFor } from '../lib/notification-source-keys';

interface HistoryItem {
  id: number;
  level?: string | null;
  channel: string;
  stream_slug?: string | null;
  stream_title?: string | null;
  title: string;
  detail?: string | null;
  source?: string | null;
  metadata?: Record<string, unknown> | null;
  requires_ack?: boolean;
  read?: boolean;
  read_at?: string | null;
  created_at: string;
}

const CHANNELS = ['all', 'approvals', 'alert', 'system', 'comms'] as const;

function isDecidableHere(n: HistoryItem): boolean {
  const meta = n.metadata ?? {};
  return String(meta.decide_surface ?? '') === 'notification_center' &&
    String(meta.kind ?? '') === 'authority_contract' &&
    !!meta.request_id && n.read !== true;
}

function levelVariant(level?: string | null): 'error' | 'warning' | 'secondary' {
  if (level === 'error' || level === 'critical') return 'error';
  if (level === 'warn' || level === 'warning') return 'warning';
  return 'secondary';
}

export function NotificationsHistoryCard(_props: WidgetRenderProps) {
  const { t } = useTranslation();
  const [channel, setChannel] = useState<(typeof CHANNELS)[number]>('all');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [importantOnly, setImportantOnlyState] = useState(readImportantOnly);
  useEffect(() => {
    const h = () => setImportantOnlyState(readImportantOnly());
    window.addEventListener(IMPORTANT_ONLY_EVENT, h);
    window.addEventListener('storage', h);
    return () => {
      window.removeEventListener(IMPORTANT_ONLY_EVENT, h);
      window.removeEventListener('storage', h);
    };
  }, []);
  const setImportantOnly = (v: boolean) => {
    writeImportantOnly(v);
    setImportantOnlyState(v);
  };
  const qc = useQueryClient();

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['notifications-history', channel, unreadOnly],
    queryFn: () =>
      api.mcpSession<{ ok?: boolean; items?: HistoryItem[]; unread?: number }>(
        'notifications.list',
        { limit: 200, channel, unread_only: unreadOnly },
      ),
    refetchInterval: 60_000,
  });
  const rawItems = data?.items ?? [];
  const items = importantOnly ? rawItems.filter(isImportantNotification) : rawItems;
  const unreadBadge = importantOnly
    ? rawItems.filter((n) => n.read !== true && isImportantNotification(n)).length
    : (typeof data?.unread === 'number' ? data.unread : rawItems.filter((n) => n.read !== true).length);

  // Schválení/zamítnutí přímo z historie — stejná cesta jako zvoneček
  // (mesh.contracts -> castVote; po quoru operaci dokončí trigger).
  const decide = useMutation({
    mutationFn: async (p: { id: number; request_id: string; decision: 'approve' | 'deny' }) => {
      const r = await api.mcpSession<{ ok?: boolean; error?: string }>('mesh.contracts', {
        action: p.decision,
        request_id: p.request_id,
      });
      if (r && r.ok === false) throw new Error(r.error || 'decide selhalo');
      await api.mcpSession('notifications.ack', { id: p.id });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['notifications-history'] }),
  });

  // Widget kontrakt: chrome (kartu/hlavičku) kreslí WidgetShell — tady jen tělo.
  return (
    <div>
      <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
        <span className='flex items-center gap-2 text-sm text-muted-foreground'>
          <Bell className='h-4 w-4' />
          {unreadBadge > 0 && (
            <Badge variant='warning'>{unreadBadge}</Badge>
          )}
        </span>
        <div className='flex items-center gap-2'>
          <select
            className='rounded border border-border bg-background px-2 py-1 text-sm'
            value={channel}
            onChange={(e) => setChannel(e.target.value as (typeof CHANNELS)[number])}
          >
            {CHANNELS.map((c) => (
              <option key={c} value={c}>
                {c === 'all' ? t('notifications.allChannels', 'všechny kanály') : c}
              </option>
            ))}
          </select>
          <label className='flex items-center gap-1 text-sm text-muted-foreground'>
            <input
              type='checkbox'
              checked={importantOnly}
              onChange={(e) => setImportantOnly(e.target.checked)}
            />
            {t('notifications.importantOnly', 'jen důležité')}
          </label>
          <label className='flex items-center gap-1 text-sm text-muted-foreground'>
            <input
              type='checkbox'
              checked={unreadOnly}
              onChange={(e) => setUnreadOnly(e.target.checked)}
            />
            {t('notifications.unreadOnly', 'jen nepřečtená')}
          </label>
          <Button variant='ghost' size='sm' onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          </Button>
        </div>
      </div>
      <div>
        {items.length === 0
          ? (
            <div className='py-6 text-center text-sm text-muted-foreground'>
              {t('notifications.historyEmpty', 'Žádná oznámení pro zvolený filtr.')}
            </div>
          )
          : (
            <div className='max-h-[32rem] overflow-y-auto'>
              <table className='w-full text-sm'>
                <thead className='sticky top-0 bg-background text-left text-muted-foreground'>
                  <tr>
                    <th className='py-1 pr-3'>{t('notifications.colWhen', 'Kdy')}</th>
                    <th className='py-1 pr-3'>{t('notifications.colChannel', 'Kanál')}</th>
                    <th className='py-1 pr-3'>{t('notifications.colWhat', 'Co')}</th>
                    <th className='py-1'>{t('notifications.colState', 'Stav')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((n) => {
                    const kind = String(n.metadata?.kind ?? '');
                    const requestId = String(n.metadata?.request_id ?? '');
                    // Jazyk vybírá čtenář, ne zapisovatel: uložená věta je fallback.
                    const text = notificationText(n, t, { sourceKey: sourceKeyFor });
                    return (
                      <tr key={n.id} className='border-t border-border/50 align-top'>
                        <td className='whitespace-nowrap py-1.5 pr-3 text-muted-foreground'>
                          {new Date(n.created_at).toLocaleString()}
                        </td>
                        <td className='py-1.5 pr-3'>
                          <Badge variant={levelVariant(n.level)}>{n.channel}</Badge>
                        </td>
                        <td className='py-1.5 pr-3'>
                          <div className='font-medium'>{text.title}</div>
                          {text.detail && (
                            <div className='line-clamp-2 text-muted-foreground'>{text.detail}</div>
                          )}
                          {(kind || requestId) && (
                            <div className='text-xs text-muted-foreground'>
                              {kind && <span className='mr-2'>{kind}</span>}
                              {requestId && <code>{requestId}</code>}
                            </div>
                          )}
                        </td>
                        <td className='whitespace-nowrap py-1.5'>
                          {isDecidableHere(n) && (
                            <span className='mr-2 inline-flex gap-1'>
                              <Button
                                size='sm'
                                variant='success'
                                disabled={decide.isPending}
                                onClick={() =>
                                  decide.mutate({
                                    id: n.id,
                                    request_id: String(n.metadata?.request_id ?? ''),
                                    decision: 'approve',
                                  })}
                              >
                                {t('notifications.approve', 'Schválit')}
                              </Button>
                              <Button
                                size='sm'
                                variant='danger'
                                disabled={decide.isPending}
                                onClick={() =>
                                  decide.mutate({
                                    id: n.id,
                                    request_id: String(n.metadata?.request_id ?? ''),
                                    decision: 'deny',
                                  })}
                              >
                                {t('notifications.deny', 'Zamítnout')}
                              </Button>
                            </span>
                          )}
                          {n.read
                            ? (
                              <span className='text-muted-foreground'>
                                {t('notifications.stateRead', 'přečtené')}
                              </span>
                            )
                            : n.requires_ack
                            ? (
                              <Badge variant='warning'>
                                {t('notifications.stateAction', 'čeká na akci')}
                              </Badge>
                            )
                            : <Badge variant='secondary'>{t('notifications.stateNew', 'nové')}</Badge>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
}

export const NOTIFICATIONS_WIDGETS: WidgetDef[] = [
  {
    id: 'notifications.history',
    title: 'Historie oznámení',
    module: 'notifications',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: Bell,
    since: '2.2.1',
    updated: '2026-08-23',
    component: NotificationsHistoryCard,
  },
];
